<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { onMounted } from 'vue'
import LibrarySidebarMenu from '@/library/LibrarySidebarMenu.vue'
import ProgressSpinner from 'primevue/progressspinner'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import RecentlySavedContentSections from '@/library/recently_saved/RecentlySavedContentSections.vue'
import SelectGroup from '@/componentlibrary/SelectGroup.vue'
import { useRecentlySavedStore } from '@/library/recently_saved/recently_saved.store'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import { useUserStore } from '@/authentication/user.store'
import PremiumActionModal from '@/components/PremiumActionModal.vue'

const analytics = getAnalyticsTracker()
const recentlySavedStore = useRecentlySavedStore()
const userStore = useUserStore()
const { loading, loadError, selectedSubject, possibleSubjects } = storeToRefs(recentlySavedStore)
const { isLicenseExpired } = storeToRefs(userStore)

onMounted(async function () {
  if (!isLicenseExpired.value) {
    await recentlySavedStore.fetchRecentlySavedContent()
  }
  analytics.sendEvent('Viewed Recently Saved')
})

const handleSubjectFilterSelect = (subject: string) => {
  analytics.sendEvent('Selected Recently Saved Subject Filter', { filter: subject })
}
</script>
<template>
  <div class="container">
    <div class="grid grid-cols-12 gap-4 pt-12">
      <div class="col-span-12 py-4 pl-4 pr-8 md:col-span-4 lg:col-span-3">
        <LibrarySidebarMenu />
      </div>
      <div class="col-span-12 p-4 md:col-span-8 lg:col-span-9">
        <h1 class="header-md font-medium">Recently Saved</h1>
        <PremiumActionModal />
        <div v-if="loading" class="inline-flex items-center pt-2">
          <ProgressSpinner strokeWidth="8" class="h-5 w-5 stroke-primary-400" />
          <span class="ml-2">Loading resources...</span>
        </div>
        <GbInlineMessage gbTheme="grayDark" class="mt-8" v-else-if="!loading && loadError">
          <template #icon><span class="material-icons-round"> info_outline </span></template>
          We're not able to retrieve your recently saved content. Try again later.
        </GbInlineMessage>
        <div v-else>
          <div class="recently-saved">
            <div class="grid grid-cols-1 md:grid-cols-3">
              <SelectGroup
                v-if="possibleSubjects.length > 1"
                v-model="selectedSubject"
                :filterGroup="possibleSubjects"
                groupLabel="Subject"
                placeholder="All Subjects"
                @update:modelValue="handleSubjectFilterSelect"
              />
            </div>
            <div
              v-if="
                recentlySavedStore.hasNoContent ||
                recentlySavedStore.hasRecentlySaved ||
                (!recentlySavedStore.hasRecentlySaved &&
                  recentlySavedStore.hasPreviouslySaved &&
                  !selectedSubject)
              "
            >
              <hr class="border-gray-400" />
              <h2 class="py-2 text-lg font-medium">Saved in the last 30 days</h2>
              <p
                class="mb-20 mt-14 text-center italic"
                v-if="!recentlySavedStore.hasRecentlySaved"
              >
                You haven't saved any content in the last 30 days.
              </p>
            </div>
            <RecentlySavedContentSections
              v-if="recentlySavedStore.hasRecentlySaved"
              :contentObjectsByType="recentlySavedStore.recentlySaved"
            />
          </div>
          <div
            class="previously-saved pt-8"
            v-if="!loadError && recentlySavedStore.hasPreviouslySaved"
          >
            <hr class="border-10 border-gray-400" />
            <h2 class="py-2 text-lg font-medium">Saved more than 30 days ago</h2>
            <RecentlySavedContentSections
              :contentObjectsByType="recentlySavedStore.previouslySaved"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
